import { i18n } from '@/shared/i18n';
import type { SortChipOption } from '@/shared/ui';

// Shared between the page header and the true-empty state so the copy can't drift.
// Functions, not consts: resolving at call time is what lets the copy follow a
// language change instead of freezing at import.
export const libraryTitle = (): string => i18n.t('projects:projectDetail.libraryConstants.library');
export const libraryDescription = (): string =>
  i18n.t('projects:projectDetail.libraryConstants.referencesKnowledgeAndOtherInput');

// Workspace-scoped palette + filter choices + sort helpers were lifted to
// `entities/artifact` so multiple surfaces (Library, brief-generation
// intake, ...) can consume them without crossing into a page slice.
// Re-exported here for back-compat — existing Library consumers
// (`ArtifactCard`, `ArtifactsToolbar`, ...) keep their import paths.
export { ARTIFACT_KIND_PALETTE, ARTIFACT_KIND_FILTER_CHOICES } from '@/entities/artifact';
// Multiple of 6 so a page fills cleanly at both the 2- and 3-column grid
// widths (no half-empty last row). See ArtifactsGrid.
export const LIBRARY_PAGE_SIZE = 18;

// Project-scoped artifact sort — STAYS HERE.
//
// The project-artifacts endpoint expects lowercase `sortOrder` ('asc' /
// 'desc') and a `sortBy` set that does NOT match the workspace endpoint
// (`attachedAt` is project-only). Lifting this to `entities/artifact`
// would conflate two different BE contracts. Normalising the BE is the
// proper long-term fix; until then this list lives next to the
// `ArtifactsToolbar` that consumes it.

export type ArtifactSortKey = 'name' | 'attachedAt';
export type ArtifactSortDirection = 'asc' | 'desc';

interface ArtifactSortChoice extends SortChipOption {
  sortBy: ArtifactSortKey;
  sortOrder: ArtifactSortDirection;
}

// Labels are getters so each read resolves against the active locale; a plain
// `i18n.t(...)` here would freeze the English string at import time.
export const ARTIFACT_SORT_CHOICES: ReadonlyArray<ArtifactSortChoice> = [
  {
    id: 'attachedAt-asc',
    get label() {
      return i18n.t('projects:projectDetail.libraryConstants.oldestFirst');
    },
    sortBy: 'attachedAt',
    sortOrder: 'asc',
    direction: 'asc',
  },
  {
    id: 'name-asc',
    get label() {
      return i18n.t('projects:projectDetail.libraryConstants.nameAZ');
    },
    sortBy: 'name',
    sortOrder: 'asc',
    direction: 'asc',
  },
  {
    id: 'name-desc',
    get label() {
      return i18n.t('projects:projectDetail.libraryConstants.nameZA');
    },
    sortBy: 'name',
    sortOrder: 'desc',
    direction: 'desc',
  },
];

export function resolveArtifactSort(
  id: string | null,
): { sortBy: ArtifactSortKey; sortOrder: ArtifactSortDirection } | null {
  if (id === null) return null;
  const choice = ARTIFACT_SORT_CHOICES.find((c) => c.id === id);
  return choice ? { sortBy: choice.sortBy, sortOrder: choice.sortOrder } : null;
}
